import { useState } from 'react';
import { Alert, Button, Popconfirm, Segmented } from 'antd';
import { CalendarOutlined, SendOutlined } from '@ant-design/icons';
import ScheduleTimePicker from './ScheduleTimePicker';
import { formatTaipeiMinute, validateScheduleTime } from '../../lib/schedule';
import type { SaveIntent } from '../../hooks/useArticleEditor';

type Mode = 'now' | 'schedule';

interface Props {
  status: string;
  scheduledAt: number | null;
  /** 進行中的動作（用於按鈕 loading / 停用）。 */
  pending: SaveIntent['kind'] | null;
  onAction: (intent: SaveIntent) => void;
  onInvalid: (msg: string) => void;
}

const MODE_OPTS = [
  { label: '立即發佈', value: 'now' as const },
  { label: '預排時間', value: 'schedule' as const },
];

/** 發佈方式：「立即發佈」或「預排時間」二選一；狀態只會由此處的按鈕改變。 */
export default function PublishModePanel({ status, scheduledAt, pending, onAction, onInvalid }: Props) {
  const isScheduled = status === 'scheduled' && !!scheduledAt;
  const [mode, setMode] = useState<Mode>(isScheduled ? 'schedule' : 'now');
  const [picked, setPicked] = useState<number | null>(scheduledAt);
  const busy = pending !== null;

  const confirmSchedule = () => {
    const err = validateScheduleTime(picked);
    if (err || picked == null) return onInvalid(err ?? '請先選擇排程刊登時間');
    onAction({ kind: 'schedule', at: picked });
  };

  return (
    <div className="flex flex-col gap-2">
      {isScheduled && (
        <Alert
          type="info"
          showIcon
          icon={<CalendarOutlined />}
          message={`已排程：${formatTaipeiMinute(scheduledAt)} 自動發佈`}
        />
      )}
      <Segmented<Mode> block value={mode} options={MODE_OPTS} onChange={setMode} />
      {mode === 'now' ? (
        <>
          <Popconfirm
            title="確定立即發佈？"
            description={isScheduled ? '將取消原排程，文章馬上在前台上線。' : '文章將馬上在前台上線。'}
            okText="確認發佈"
            cancelText="取消"
            onConfirm={() => onAction({ kind: 'publish' })}
          >
            <Button type="primary" block icon={<SendOutlined />} loading={pending === 'publish'} disabled={busy}>
              確認發佈
            </Button>
          </Popconfirm>
          <p className="m-0 text-xs text-[#8a94a6]">「確認發佈」會先儲存目前內容，並馬上在前台上線。</p>
        </>
      ) : (
        <>
          <ScheduleTimePicker value={picked} onChange={setPicked} />
          <Button
            type="primary"
            block
            icon={<CalendarOutlined />}
            loading={pending === 'schedule'}
            disabled={busy || !picked || (isScheduled && picked === scheduledAt)}
            onClick={confirmSchedule}
          >
            確認排程
          </Button>
          <p className="m-0 text-xs text-[#8a94a6]">
            {isScheduled
              ? '要改時間：選擇新時間後再按「確認排程」；要馬上上線請切到「立即發佈」。'
              : '「確認排程」會儲存目前內容，並於指定時間（台北時間）自動發佈。'}
          </p>
        </>
      )}
    </div>
  );
}
